import React from "react";
import { 
  Palette, 
  TrendingUp, 
  Star, 
  GraduationCap, 
  FileSignature, 
  Activity 
} from "lucide-react";
import { clientFeed } from "../utils/mockData";
import useIntersectionObserver from "../hooks/useIntersectionObserver";

const getIcon = (iconName) => {
  const icons = {
    Palette,
    TrendingUp,
    Star,
    GraduationCap,
    FileSignature,
  };
  return icons[iconName] || Activity;
};

const getTypeColor = (type) => {
  switch (type) {
    case "new_project": return "bg-blue-500";
    case "milestone": return "bg-green-500";
    case "review": return "bg-yellow-500";
    case "training": return "bg-purple-500";
    default: return "bg-gray-500";
  }
};

export const ClientFeedSection = () => {
  const [sectionRef, sectionInView] = useIntersectionObserver();

  return (
    <section className="py-20 bg-white dark:bg-[#0a0a0a] border-y border-gray-100 dark:border-gray-800">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-3 gap-12 lg:gap-8">
          
          {/* Left Column: Heading & CTA */}
          <div 
            ref={sectionRef}
            className={`lg:col-span-1 space-y-6 transition-all duration-700 ${
              sectionInView ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"
            }`}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-sm font-medium animate-pulse">
              <span className="w-2 h-2 rounded-full bg-green-500"></span>
              Live Activity
            </div>
            
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white leading-tight">
              Selalu Ada yang Baru di Dreams Marketing
            </h2>
            
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              Setiap hari kami membantu brand bertumbuh, mencapai milestone baru, dan menciptakan dampak nyata.
            </p>
            
            <button className="bg-[#D32F2F] hover:bg-[#b71c1c] text-white px-8 py-3 rounded-xl font-medium transition-all shadow-lg hover:shadow-xl hover:-translate-y-1">
              Bergabung Sekarang
            </button>
          </div>

          {/* Right Column: Feed */}
          <div className="lg:col-span-2">
            <div 
              className="relative space-y-4 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar"
              role="feed"
              aria-label="Recent client activities"
            >
              {/* Vertical line connector */}
              <div className="absolute left-6 top-8 bottom-8 w-0.5 bg-gray-200 dark:bg-gray-800 hidden sm:block"></div>

              {clientFeed.map((item, index) => {
                const Icon = getIcon(item.icon);
                const typeColor = getTypeColor(item.type);
                
                return (
                  <div 
                    key={item.id}
                    className="relative flex items-start gap-4 p-4 rounded-xl bg-gray-50 dark:bg-[#121212] border border-transparent hover:border-gray-200 dark:hover:border-gray-800 transition-all duration-300 animate-fade-in-up"
                    style={{ animationDelay: `${index * 150}ms` }}
                  >
                    <div className={`relative z-10 shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white shadow-md ${typeColor}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap justify-between items-start gap-2 mb-1">
                        <h4 className="font-bold text-gray-900 dark:text-white truncate">
                          {item.client}
                        </h4>
                        <time className="text-xs text-gray-400 font-medium whitespace-nowrap">
                          {item.timestamp}
                        </time>
                      </div>
                      
                      <p className="text-gray-600 dark:text-gray-300 text-sm">
                        {item.message}
                      </p>
                      
                      <div className="mt-2 flex items-center gap-2">
                         <span className={`w-1.5 h-1.5 rounded-full ${typeColor}`}></span>
                         <span className="text-xs text-gray-500 dark:text-gray-500 font-medium uppercase tracking-wide">
                            {item.type.replace('_', ' ')}
                         </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
        </div>
      </div>
    </section>
  );
};
